/**
 * Attribution Metadata Types
 *
 * Shared types for tracking how users arrive at the site and what
 * campaigns/channels drive conversions. Attached to every form
 * submission and forwarded to the Route Royal CRM.
 */

// ---------------------------------------------------------------------------
// UTM Parameters
// ---------------------------------------------------------------------------

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

export type UtmKey = (typeof UTM_KEYS)[number];
export type UtmParams = Partial<Record<UtmKey, string>>;

// ---------------------------------------------------------------------------
// Ad-platform Click IDs
// ---------------------------------------------------------------------------

export const CLICK_ID_KEYS = [
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'li_fat_id',
  'wbraid',
  'gbraid',
  'twclid',
] as const;

export type ClickIdKey = (typeof CLICK_ID_KEYS)[number];
export type ClickIds = Partial<Record<ClickIdKey, string>>;

// ---------------------------------------------------------------------------
// Email Campaign Identifiers
// ---------------------------------------------------------------------------

export const EMAIL_CAMPAIGN_KEYS = ['eid', 'uid', 'email_campaign_id'] as const;
export type EmailCampaignKey = (typeof EMAIL_CAMPAIGN_KEYS)[number];
export type EmailCampaignIds = Partial<Record<EmailCampaignKey, string>>;

// ---------------------------------------------------------------------------
// Attribution Metadata (attached to every lead / form submission)
// ---------------------------------------------------------------------------

export interface AttributionMetadata {
  [key: string]: unknown;

  /** First-touch UTM params (persisted in localStorage with 30-day expiry) */
  firstTouch?: UtmParams;

  /** Last-touch / current-session UTM params (sessionStorage) */
  lastTouch?: UtmParams;

  /** Ad-platform click identifiers */
  clickIds?: ClickIds;

  /** Email campaign identifiers from mailer links */
  emailCampaign?: EmailCampaignIds;

  /** First-party visitor ID from rr_vid cookie */
  visitorId?: string;

  /** Authenticated customer ID (Route Royal IndividualProfile ID) */
  userId?: string;

  /** GA4 client_id extracted from the _ga cookie */
  gaClientId?: string;

  /** The page URL where the conversion happened */
  conversionUrl?: string;

  /** The landing page URL for this session */
  landingPage?: string;

  /** document.referrer captured on first page load */
  referrer?: string;

  /** ISO timestamp of when the attribution was collected */
  timestamp?: string;
}

// ---------------------------------------------------------------------------
// Storage Keys
// ---------------------------------------------------------------------------

export const STORAGE_KEYS = {
  UTM_FIRST_TOUCH: 'rr_utm_first',
  UTM_LAST_TOUCH: 'rr_utm_params',
  CLICK_IDS: 'rr_click_ids',
  EMAIL_CAMPAIGN: 'rr_email_campaign',
  LANDING_PAGE: 'rr_landing_page',
  REFERRER: 'rr_referrer',
  FIRST_TOUCH_EXPIRY: 'rr_utm_first_exp',
} as const;

/**
 * Canonical first-party visitor cookie, shared with the Route Royal SDK.
 * The legacy `rr_vid` cookie is still read for continuity with visitors who
 * were issued an identity before the cookie name was unified.
 */
export const VISITOR_ID_COOKIE = '_rr_id';
export const LEGACY_VISITOR_ID_COOKIE = 'rr_vid';

/** First-touch attribution expiry in milliseconds (30 days) */
export const FIRST_TOUCH_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;
